import {computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import errorToast from "../../../components/toast/ErrorToast";
import {default as handleSuccessToast} from "../../../components/toast/SuccessToast";
import LegalEntityPanelFeature from "./LegalEntityPanelFeature";
import CustomerContactPanelFeature from "./CustomerContactPanelFeature";
import CustomerOnboardingDeliverySitesOnboardingRequestsFeature
    from "./CustomerOnboardingDeliverySitesOnboardingRequestsFeature";
import CommercialTermsFeature from "./CommercialTermsFeature";
import CustomerOnboardingNewUsersFeature from "./CustomerOnboardingNewUsersFeature";
import TaxDataPanelFeature from "./TaxDataPanelFeature";
import FinanceDataPanelFeature from "./FinanceDataPanelFeature";

export default class CustomerOnboardingEditionFeature {

    constructor(application, navigator, taxpayerIdentificationNumber) {
        this._application = application;
        this._navigate = navigator;
        this._taxpayerIdentificationNumber = taxpayerIdentificationNumber;
        this._customerOnboarding = null;
        this._loaded = false;
        this._features = {}

        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            _features: observable,
            features: computed,
            _taxpayerIdentificationNumber: observable,
            _customerOnboarding: observable,
            customerOnboarding: computed,
        })
    }

    async initializeFeatures(){
        this._features['LegalEntityPanelFeature'] = new LegalEntityPanelFeature(this._application, this)
        this._features['CustomerContactPanelFeature'] = new CustomerContactPanelFeature(this._application, this)
        this._features['CustomerOnboardingDeliverySitesOnboardingRequestsFeature'] = new CustomerOnboardingDeliverySitesOnboardingRequestsFeature(this._application, this)
        this._features['CommercialTermsFeature'] = new CommercialTermsFeature(this._application, this)
        this._features['CustomerOnboardingNewUsersFeature'] = new CustomerOnboardingNewUsersFeature(this._application, this)
        this._features['TaxDataPanelFeature'] = new TaxDataPanelFeature(this._application, this)
        this._features['FinanceDataPanelFeature'] = new FinanceDataPanelFeature(this._application, this)
        await Promise.all(Object.values(this._features).map(feature => feature.load()));
    }

    _updateFeatures(){
        Object.values(this._features).map(feature => feature.createForm())
    }

    async load() {
        await this.getCustomerOnboarding()
        await this.initializeFeatures()
        this.finishLoading()
    }

    finishLoading() {
        this._loaded = true
    }

    get features(){
        return this._features
    }

    set features(aValue){
        return this._features = aValue
    }

    get loaded() {
        return this._loaded;
    }

    set loaded(value) {
        this._loaded = value;
    }

    get taxpayerIdentificationNumber() {
        return this._taxpayerIdentificationNumber;
    }

    async getCustomerOnboarding() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._customerOnboarding = response.customerOnboarding();
                return this._customerOnboarding
            },
            handlesError: (response) => {
               errorToast('No se pudo obtener la información sobre el proceso de alta del cliente')
            }
        });

        return this._application.apiClient().getCustomerOnboarding(responseHandler, this._taxpayerIdentificationNumber);
    }

    async updateCustomerOnboardingState() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                handleSuccessToast("Ha avanzado el proceso de alta. Se ha notificado a las áreas correspondientes");
                await this.customerOnboardingHasChanged()
                this._updateFeatures()
            },
            handlesError: (response) => {
                if (response.hasError()) {
                    const error_code = response.errors()[0].code
                    if (error_code === 'cannot_advance_customer_onboarding_with_mandatory_fields_uncompleted'){
                        errorToast('No se ha podido avanzar el proceso de alta. Hay campos obligatorios sin completar')
                    }
                    else if (error_code === 'cannot_advance_customer_onboarding_without_marketplace_administrator_user'){
                        errorToast('No se ha podido avanzar el proceso de alta. Debe haber al menos un usuario con rol Administrador Marketplace')
                    }
                    else {
                        errorToast('No se pudo obtener la información sobre el proceso de alta del cliente')
                    }
                }
            }
        });
        return this._application.apiClient().updateCustomerOnboardingState(responseHandler, this._taxpayerIdentificationNumber);
    }

    async editOrCreateCustomerOnboardingAuthorizations() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                handleSuccessToast("Se han creado las solicitudes de autorización para las áreas correspondientes");
                await this.customerOnboardingHasChanged()
                this._updateFeatures()
            },
            handlesError: (response) => {
                if (response.hasError()) {
                    const error_code = response.errors()[0].code
                    if (error_code === 'cannot_advance_customer_onboarding_with_mandatory_fields_uncompleted'){
                        errorToast('No se ha podido crear las autorizaciones. Hay campos obligatorios sin completar')
                    }
                    else {
                        errorToast('No se pudieron crear las solicitudes de autorización')
                    }
                }
            }
        });
        return this._application.apiClient().editOrCreateCustomerOnboardingAuthorizations(responseHandler, this._taxpayerIdentificationNumber);
    }

    get customerOnboarding() {
        return this._customerOnboarding;
    }

    async customerOnboardingHasChanged() {
        await this.getCustomerOnboarding()
    }

    isComplete(){
        return Object.values(this._features).every(feature => feature.isComplete());
    }

    canAdvanceCustomerOnboarding() {
        // This boolean must be calculated in the server side
        if (this.isProcessingByArea()) {
            const hasLogisticsPermission = this._application.permissions().includes('LOGISTICS_PERMISSION');
            const hasCommercialPermission = this._application.permissions().includes('COMMERCIAL_PERMISSION');
            const hasFinancePermission = this._application.permissions().includes('FINANCE_PERMISSION');
            const hasTaxPermission = this._application.permissions().includes('TAX_PERMISSION');
            const hasAnyAreaPermission = hasLogisticsPermission || hasCommercialPermission || hasFinancePermission || hasTaxPermission;

            if (this._existsAnAuthorization()) {
                let ownAuthorizations = []
                if (hasLogisticsPermission) {
                    ownAuthorizations.push(this.customerOnboarding.authorizations.logisticsAuthorizationState)
                }
                if (hasCommercialPermission) {
                    ownAuthorizations.push(this.customerOnboarding.authorizations.commercialAuthorizationState)
                }
                if (hasFinancePermission) {
                    ownAuthorizations.push(this.customerOnboarding.authorizations.financeAuthorizationState)
                }
                if (hasTaxPermission) {
                    ownAuthorizations.push(this.customerOnboarding.authorizations.taxAuthorizationState)
                }
                const existsAnOwnAuthorization = ownAuthorizations.some((authorization) => authorization !== null);
                if (existsAnOwnAuthorization) {
                    const someOwnAuthorizationRequiresChanges = ownAuthorizations.some((authorization) => authorization === 'Changes Required');
                    if (someOwnAuthorizationRequiresChanges) {
                        return this.isComplete()
                    }
                    const anOwnAuthorizationIsNotAlreadyCreated = ownAuthorizations.some((authorization) => authorization === null);
                    return anOwnAuthorizationIsNotAlreadyCreated && this.isComplete()
                }
                const canCreateAuthorizations = hasAnyAreaPermission && this.isComplete()
                return canCreateAuthorizations
            }

            return hasAnyAreaPermission && this.isComplete()
        }
        return !this.isReadOnly() && this.isComplete()
    }

    authorizationObservationRows() {
        const areas = [
            {area: 'Comercial', state: this.customerOnboarding.authorizations.commercialAuthorizationState, observations: this.customerOnboarding.authorizationsObservations.commercialAuthorizationObservations},
            {area: 'Logística', state: this.customerOnboarding.authorizations.logisticsAuthorizationState, observations: this.customerOnboarding.authorizationsObservations.logisticsAuthorizationObservations},
            {area: 'Finanzas', state: this.customerOnboarding.authorizations.financeAuthorizationState, observations: this.customerOnboarding.authorizationsObservations.financeAuthorizationObservations},
            {area: 'Impositiva', state: this.customerOnboarding.authorizations.taxAuthorizationState, observations: this.customerOnboarding.authorizationsObservations.taxAuthorizationObservations},
        ];
        return areas.filter(row => row.observations)
    }

    hasAuthorizationObservations() {
        return this.authorizationObservationRows().length > 0
    }

    _existsAnAuthorization() {
        return Object.values(this.customerOnboarding.authorizations).some(authorizationState => authorizationState !== null)
    }

    _allAuthorizationsAreCreated() {
        return Object.values(this.customerOnboarding.authorizations).every(authorizationState => authorizationState !== null)
    }

    _someAuthorizationRequiresChanges() {
        return Object.values(this.customerOnboarding.authorizations).some(authorizationState => authorizationState === 'Changes Required')
    }

    advanceCustomerOnboarding() {
        if (this.isProcessingByArea()){
            return this.editOrCreateCustomerOnboardingAuthorizations()
        }
        return this.updateCustomerOnboardingState();
    }

    isProcessingByArea() {
        return this.customerOnboarding && this.customerOnboarding.state === 'Proceso de Area'
    }

    isClosed() {
        return this.customerOnboarding.state === 'Cerrado'
    }

    isPendingErpLoad() {
        return this.customerOnboarding.state === 'Pendiente de carga en el ERP'
    }

    isReadOnly() {
        return this.isClosed() || this.isPendingErpLoad()
    }
}